import type { ReactElement, ReactNode } from 'react';
import { isValidElement, useRef, useState } from 'react';
import { useFocus, useFocusVisible, useHover } from 'react-aria';
import { useMatches } from 'react-router';
import { enableNewNavigation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import {
  Breadcrumb as Breadcrumb_,
  Breadcrumbs as Breadcrumbs_,
  Link,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import clsx from 'clsx';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { useSidebarContext } from '../Sidebar';

import styles from './Breadcrumbs.module.css';

const reactElementSchema = z.custom<ReactElement>((val) => isValidElement(val), {
  message: 'Expected a React element',
});

const breadcrumbFunctionSchema = z
  .function()
  .args(z.object({ data: z.any() }))
  .returns(z.union([z.string(), reactElementSchema, z.null(), z.undefined()]));

const breadcrumbSchema = z.object({
  breadcrumb: z.union([z.string(), breadcrumbFunctionSchema, reactElementSchema]),
});

type Item = {
  // id is used by react-aria-components under the hood
  // eslint-disable-next-line react/no-unused-prop-types
  id: string;
  text?: string | ReactNode;
  href: string;
};

export function Breadcrumbs() {
  const matches = useMatches();
  const sidebar = useSidebarContext();

  const crumbs = matches.reduce((acc, match) => {
    const parse = breadcrumbSchema.safeParse(match.handle);
    if (!parse.success) {
      return acc;
    }

    const {
      data: { breadcrumb },
    } = parse;
    let text;

    if (typeof breadcrumb === 'function') {
      if (!match.loaderData) {
        return acc;
      }
      text = breadcrumb({ data: match.loaderData });
    } else {
      text = breadcrumb;
    }

    if (text === null || text === undefined || text === '') {
      return acc;
    }

    acc.push({
      id: match.id,
      text,
      href: match.pathname,
    });
    return acc;
  }, new Array<Item>());

  return (
    <QuietErrorBoundary severity="medium">
      <Breadcrumbs_
        items={crumbs}
        className={clsx(styles.breadcrumbs, {
          [styles.withSidebar]: sidebar.isVisible,
          [styles.newNavigation]: enableNewNavigation(),
        })}
      >
        {(item) => <Breadcrumb {...item} />}
      </Breadcrumbs_>
    </QuietErrorBoundary>
  );
}

const MAX_CHARS = 64;

function Breadcrumb(item: Item) {
  const { href, text } = item;

  const innerRef = useRef<HTMLSpanElement>(null);
  const { ref, hasOverflow, props: overflowProps } = useTextOverflow<HTMLSpanElement>();
  const { hoverProps, isHovered } = useHover({});
  const [isFocused, setFocused] = useState(false);
  const { focusProps } = useFocus({
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });
  const { isFocusVisible } = useFocusVisible();

  if (isValidElement(text)) {
    return <Breadcrumb_ className={clsx(styles.crumb, styles.shrink)}>{text}</Breadcrumb_>;
  }

  return (
    <Breadcrumb_
      className={clsx(styles.crumb, {
        [styles.shrink]: typeof text === 'string' && text.length < MAX_CHARS,
      })}
    >
      <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && (isHovered || (isFocusVisible && isFocused))}>
        {/* @ts-expect-error react-aria-components and react-aria are in disagreement about the event type here, but it's legit. */}
        <Link href={href} {...overflowProps} {...focusProps} ref={ref}>
          <span ref={innerRef} {...hoverProps}>
            {text}
          </span>
        </Link>
        <Tooltip className={styles.itemText} triggerRef={ref} placement="bottom start">
          {text}
        </Tooltip>
      </TooltipTrigger>
    </Breadcrumb_>
  );
}
